// Page identity and slide-direction rules for the page transitions in
// AppRoot. Pure functions of URL paths (plus the album date registry), so the
// carousel behaves identically for link clicks and browser back/forward.

import { albumDate } from './albumDates';

export type SlideDirection = 'back' | 'forward';

// Identity of a "page" for transition purposes. It changes on real page changes
// — including switching between two albums — but NOT when a lightbox photo opens
// or closes (those just append a photo segment to the URL). The outlet is keyed
// on this, so only real page changes remount and slide. The paged lists
// (favourites, drive folders) also count `?page=` as a page, so paging slides
// too; pass the path *with* its search for those.
export const pageKey = (path: string): string => {
    const clean = path.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/';
    const seg = clean.replace(/^\//, '').split('/');
    switch (seg[0]) {
        case 'album':
            // Distinguish albums, but drop a trailing `/photo/:id` lightbox segment.
            return clean.replace(/\/photo\/[^/]+$/, '');
        case 'liked-photos':
            return '/liked-photos' + pageSuffix(path); // /liked-photos/:photo is a lightbox
        case 'p':
            // drive folder; /p/:folder/:photoId is a lightbox
            return '/p/' + (seg[1] ?? '') + pageSuffix(path);
        case 'photoshoots':
            // Gallery (/photoshoots) and album overview (/photoshoots/albums) are
            // distinct pages that should slide between each other.
            return seg[1] === 'albums' ? '/photoshoots/albums' : '/photoshoots';
        case 'tag':
            return '/tag/' + (seg[1] ?? ''); // distinct per tag (e.g. /tag/concert)
        default:
            return '/' + (seg[0] ?? ''); // one key per section (home, year, concerts, …)
    }
};

// The `?page=` number of a paged list (1 when absent), and its pageKey suffix.
// Page 1 has no suffix, so `/liked-photos` and `/liked-photos?page=1` are one
// page. A deep link to a photo on a later page (`/liked-photos/123`, no
// `?page=`) is keyed as page 1 although it shows the photo's page: paging from
// there may slide the wrong way, or (to page 1) not slide but swap in place.
const pageOf = (path: string): number => {
    const n = Number(path.match(/[?&]page=(\d+)/)?.[1] ?? 1);
    return n > 1 ? n : 1;
};
const pageSuffix = (path: string): string => {
    const n = pageOf(path);
    return n > 1 ? `?page=${n}` : '';
};

// The 4-digit year of a `/{year}` page, or null for any other path.
const yearOf = (path: string): number | null => {
    const seg = path.replace(/^\//, '').split(/[/?#]/)[0] ?? '';
    return /^\d{4}$/.test(seg) ? Number(seg) : null;
};

// Left-to-right position of each top-level page in the navbar. The slide
// direction is derived from this: navigating to a page further right slides
// forward (new page in from the right), further left slides back (in from the
// left) — regardless of whether it's a click or a browser back/forward.
// Keep in sync with the nav order in templates/base.html.twig.
const navOrder = (path: string): number => {
    const seg = path.replace(/^\//, '').split(/[/?#]/)[0] ?? '';
    if (seg === '') return 0; // Home
    // Drive site (separate route table): the index sits before folders, so a
    // folder's "Terug naar index" slides back and opening a folder slides forward.
    if (seg === 'drive') return 0; // Drive index
    if (seg === 'p') return 1; // Drive folder
    if (/^\d{4}$/.test(seg)) return 1; // year index (the "Events" link -> /{year})
    if (seg === 'collection') return 1; // album-list overview
    if (seg === 'concerts') return 2; // Concerten (gallery)
    // The concert album overview sits just after the concert gallery; other tag
    // overviews stay generic.
    if (seg === 'tag') return /^\/tag\/concert(\/|$)/.test(path) ? 2.5 : 1;
    // Portretten: the album overview sits just after the gallery, so
    // gallery -> overview slides forward and overview -> gallery slides back.
    if (seg === 'photoshoots') return /^\/photoshoots\/albums/.test(path) ? 3.5 : 3;
    if (seg === 'liked-photos') return 4; // Favorieten
    if (seg === 'upcoming-events') return 5; // Agenda
    if (seg === 'contact') return 6; // Contact
    // An album detail is a drill-in — always "deeper" than any list it's opened
    // from (year, tag/concert, photoshoots…), so entering it slides forward and
    // leaving it (browser back / "Terug naar index") slides back.
    if (seg === 'album') return 9;
    return 7; // everything else (profile, login, 404) sits to the right
};

// Which way to slide going fromPath -> toPath, using the page keys — never the
// history direction. Priority:
//   1. A link may force it via `state={{ slide: 'back' | 'forward' }}`.
//   2. Between two `/{year}` pages, follow the timeline: older = forward,
//      newer = back. Between two pages of one paged list, a higher page
//      number = forward.
//   3. Between two albums (e.g. related albums), order by date: a newer album
//      = back, an older one = forward. Dates are looked up from the registry
//      (albumDates); when one is unknown, fall back to id order as a proxy.
//   4. Otherwise navbar left-to-right order: a page further left = back.
// Applied identically to link clicks and browser back/forward, so e.g. going
// from Events back to Home always slides back regardless of how you got there.
export const slidesBack = (fromPath: string, toPath: string, forced?: SlideDirection): boolean => {
    if (forced) return forced === 'back';
    const fromYear = yearOf(fromPath);
    const toYear = yearOf(toPath);
    if (fromYear !== null && toYear !== null) return toYear > fromYear;
    const fromList = pageKey(fromPath).replace(/\?.*$/, '');
    if (fromList === pageKey(toPath).replace(/\?.*$/, '') && pageOf(fromPath) !== pageOf(toPath)) {
        return pageOf(toPath) < pageOf(fromPath);
    }
    const fromAlbum = albumIdOf(fromPath);
    const toAlbum = albumIdOf(toPath);
    if (fromAlbum !== null && toAlbum !== null && fromAlbum !== toAlbum) {
        const fromDate = albumDate(fromAlbum);
        const toDate = albumDate(toAlbum);
        if (fromDate !== undefined && toDate !== undefined && fromDate !== toDate) {
            return toDate > fromDate; // newer album = back
        }
        return toAlbum > fromAlbum; // fallback: id order
    }
    return navOrder(toPath) < navOrder(fromPath);
};

// Pages that show the same header (PageHero) and only differ below it: every
// `/{year}`, each showcase's gallery and album overview, and the pages of a
// paged list (favourites, one drive folder). Moving between two
// pages of one group keeps the header still and slides only the content under
// it (see AnimatedOutlet / `.page-layer--keep-hero`).
const heroGroup = (path: string): string | null => {
    const clean = path.replace(/[?#].*$/, '').replace(/\/+$/, '');
    if (/^\/\d{4}$/.test(clean)) return 'year';
    if (clean === '/concerts' || clean === '/tag/concert') return 'concerts';
    if (clean === '/photoshoots' || clean === '/photoshoots/albums') return 'photoshoots';
    if (/^\/liked-photos(\/|$)/.test(clean)) return 'liked';
    const folder = clean.match(/^\/p\/([^/]+)/)?.[1];
    if (folder) return 'p:' + folder;
    return null;
};

export const sharesHero = (fromPath: string, toPath: string): boolean => {
    const group = heroGroup(fromPath);
    return group !== null && group === heroGroup(toPath);
};

// The album id of an `/album/...` page, or null. Handles the plain, tag, and
// collection URL forms (`/album/:id`, `/album/tag/:tag/album/:id`,
// `/album/collection/:c/album/:id[/:secret]`), ignoring a lightbox photo suffix.
export const albumIdOf = (path: string): number | null => {
    const clean = path.replace(/[?#].*$/, '').replace(/\/photo\/[^/]+$/, '');
    const segs = clean.replace(/^\//, '').split('/');
    if (segs[0] !== 'album') return null;
    const id = segs[segs.lastIndexOf('album') + 1];
    return id && /^\d+$/.test(id) ? Number(id) : null;
};
